import {
  booleanAttribute,
  Component,
  EventEmitter,
  HostBinding,

  Output,
  input,
  computed,
  effect,
  signal,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import {
  DateDuration,
  DateFormat,
  DateRange,
  calculateStartDate,
  differenceInDays,
  differenceInMonths,
  formatDate,
  formatDateRange,
  formatUTCDate,
  formatUTCDateRange,
  getUTCEndOfDay,
  getUTCEndOfMonth,
  getUTCStartOfDay,
  subtractDays,
  calculatedEndDate,
} from '@vendasta/galaxy/utility/date-utils';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  BusinessQuarter,
  CollapseViewportWidth,
  DateRangeDisplay,
  DateRangePresetPeriod,
  getDateRangePeriods,
  createYearOptions,
  monthOptions,
  quarterOptions,
  OVERLAY_POSITIONS,
  getCurrentQuarter,
} from './date-range-periods';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { endOfDay, endOfMonth, startOfDay } from 'date-fns';

@Component({
  selector: 'glxy-date-range-presets',
  templateUrl: './date-range-presets.component.html',
  styleUrls: ['./date-range-presets.component.scss'],
  standalone: false,
})
export class DateRangePresetsComponent {
  @HostBinding('class') class = 'glxy-date-range-presets';

  /* Whether to collapse this input when the window size is tablet-sized or large-tablet-sized */
  collapseViewportWidth = input<CollapseViewportWidth>('tablet');

  /* Whether to show date comparisons of the previous period of the selected date range */
  comparison = input(false, { transform: booleanAttribute });

  /* Whether to have a semi-transparent background on the overlay */
  showBackdrop = input(false, { transform: booleanAttribute });

  /* Whether to include today in date ranges */
  includeToday = input(false, { transform: booleanAttribute });

  /* Whether to enable future date selection */
  enableFuturePresetButtons = input(false, { transform: booleanAttribute });

  /* Whether to include future years in the datepicker */
  allowFutureYearsInDropdown = input(false);

  /* Whether to use local time instead of UTC for date range boundaries */
  useLocalTime = input(false, { transform: booleanAttribute });

  /* Whether to show the "All Time" option in the date range preset */
  showAllTimeOption = input(false, { transform: booleanAttribute });

  /* When a date range (start and end date) is selected that pair is emitted. Emits null when "All Time" is selected. */
  @Output() selectedDateRange = new EventEmitter<DateRange | null>();

  /* When a date range (start and end date) is selected the previous period date range is emitted. */
  @Output() selectedComparisonDateRange = new EventEmitter<DateRange>();

  /* When a date range period is selected that selected number is emitted. */
  @Output() selectedDateRangePeriod = new EventEmitter<DateRangePresetPeriod>();

  readonly today = new Date();
  isOpen = false;
  dateRange?: DateRange;
  comparisonDateRange?: DateRange;

  dateRangeFormGroup = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });

  get filteredDateRangePeriods() {
    if (this.showAllTimeOption()) {
      return this.DATE_RANGE_PERIODS();
    }
    return this.DATE_RANGE_PERIODS().filter((period) => period.id !== DateRangePresetPeriod.allTime);
  }

  get isAllTimeSelected(): boolean {
    return this.selectedPeriod() === DateRangePresetPeriod.allTime;
  }

  monthOptions = monthOptions;
  quarterOptions = quarterOptions;

  selectedMonthYearControl = new FormControl<number>(new Date().getFullYear());
  selectedQuarterYearControl = new FormControl<number>(new Date().getFullYear());
  selectedMonthControl = new FormControl<number>(new Date().getMonth());
  selectedQuarterControl = new FormControl<BusinessQuarter>(getCurrentQuarter());

  periodDisplay$$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  periodDisplay$: Observable<string> = this.periodDisplay$$.asObservable();

  _positions = OVERLAY_POSITIONS;

  private destroyRef = inject(DestroyRef);

  private periodManuallySet = false;

  // Computed signal for default period based on inputs
  private defaultPeriod = computed(() => {
    const datePeriod = this.defaultDatePeriod();
    if (datePeriod !== undefined) {
      return datePeriod;
    }
    // Set default period based on future date enabled state
    return this.enableFuturePresetButtons() ? DateRangePresetPeriod.nextSevenDays : DateRangePresetPeriod.lastSevenDays;
  });

  selectedPeriod = signal<DateRangePresetPeriod | undefined>(undefined);
  selectedRange: string | null = null;
  selectedQuarter = 1;
  currentYear = new Date().getFullYear();

  dateRangeDisplay: DateRangeDisplay = {
    period: null,
    range: null,
  };

  /* Date range(start and end date) to be used if custom is set for defaultDatePeriod*/
  dateRangeForCustomDefault = input<DateRange | undefined>();

  defaultDatePeriod = input<DateRangePresetPeriod | undefined>();

  formatDateRange = formatUTCDateRange;

  // Computed signal for date range periods based on enableFuturePresetButtons
  DATE_RANGE_PERIODS = computed(() => getDateRangePeriods(this.enableFuturePresetButtons()));

  // Computed signal for year options based on allowFutureYearsInDropdown
  yearOptions = computed(() => createYearOptions(this.allowFutureYearsInDropdown()));

  constructor() {
    effect(() => {
      const defaultPeriodValue = this.defaultPeriod();
      if (!this.periodManuallySet) {
        this.selectedPeriod.set(defaultPeriodValue);
        this.initializeDateRange();
      }
    });

    this.setupMonthSelectChanges();
    this.setupQuarterSelectChanges();
  }

  get selectedPeriods() {
    return DateRangePresetPeriod;
  }

  private initializeDateRange(): void {
    const period = this.selectedPeriod();
    if (period === undefined) return;

    const dateRange = this.DATE_RANGE_PERIODS().find((val) => val.id === Number(period));
    if (dateRange) {
      if (this.dateRangeForCustomDefault() && period === DateRangePresetPeriod.custom) {
        this.dateRange = this.dateRangeForCustomDefault()!;
        this.syncFormGroup();
        this.setComparisonDateRange(
          this.dateRange?.start || new Date(),
          differenceInDays(this.dateRange.end || new Date(), this.dateRange.start || new Date()) + 1,
          DateDuration.days,
        );
      } else if (period === DateRangePresetPeriod.byMonth) {
        const month = this.selectedMonthControl.value;
        const year = this.selectedMonthYearControl.value;
        if (month != null && year != null) {
          this.setMonthDateRange(month, year);
        }
      } else if (period === DateRangePresetPeriod.byQuarter) {
        const quarter = this.selectedQuarterControl.value;
        const year = this.selectedQuarterYearControl.value;
        if (quarter != null && year != null) {
          this.setQuarterDateRange(quarter, year);
        }
      } else if (period === DateRangePresetPeriod.allTime) {
        this.dateRange = undefined;
      } else {
        this.setDateRange(dateRange?.value || '');
      }
    }
    this.updateDateRangeDisplay();
  }

  private updateDateRangeDisplay(): void {
    const isAllTime = this.selectedPeriod() === DateRangePresetPeriod.allTime;

    if (!this.dateRange && !isAllTime) return;

    const period = this.DATE_RANGE_PERIODS().find((period) => period.id === Number(this.selectedPeriod()));
    this.dateRangeDisplay.period = period?.name || '';
    /* Format the date range based on the useLocalTime flag */
    if (!isAllTime && this.dateRange) {
      if (this.useLocalTime()) {
        this.dateRangeDisplay.range = formatDateRange(this.dateRange);
      } else {
        this.dateRangeDisplay.range = formatUTCDateRange(this.dateRange);
      }
    }
    this.setPeriodDisplay();
    this.emitDateRange();
    this.emitComparisonDateRange();
    this.emitDateRangePeriod();
  }

  private setupMonthSelectChanges(): void {
    this.selectedMonthControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (value == null) return;
      if (!this.dateRange?.start) return;
      this.currentYear = this.useLocalTime()
        ? this.dateRange.start.getFullYear()
        : this.dateRange.start.getUTCFullYear();
      this.setMonthDateRange(value, this.currentYear);
    });
    this.selectedMonthYearControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (value == null) return;
      this.setDateRangeYear('month', value);
    });
  }

  private setupQuarterSelectChanges(): void {
    this.selectedQuarterControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (value == null) return;
      if (!this.dateRange?.start) return;
      this.selectedQuarter = value;
      this.currentYear = this.dateRange.start.getFullYear();
      this.setQuarterDateRange(value, this.currentYear);
    });
    this.selectedQuarterYearControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (value == null) return;
      this.setDateRangeYear('quarter', value);
    });
  }

  toggleDropdown(): void {
    this.isOpen = !this.isOpen;
    this.currentYear = new Date().getFullYear();
  }

  handleOnPeriodChange(value: DateRangePresetPeriod): void {
    this.periodManuallySet = true;
    this.selectedPeriod.set(value);

    // Handle All Time case - clear date ranges so the display updates
    if (value === DateRangePresetPeriod.allTime) {
      this.dateRange = undefined;
      this.comparisonDateRange = undefined;
      return;
    }

    const datePeriod = this.DATE_RANGE_PERIODS().find((val) => val.id === Number(value));
    this.selectedRange = datePeriod?.value || null;
    if (this.selectedRange) {
      this.setDateRange(this.selectedRange);
    } else {
      if (value === DateRangePresetPeriod.byMonth) {
        // month selected
        const month = this.selectedMonthControl.value;
        const year = this.selectedMonthYearControl.value;
        if (month != null && year != null) {
          this.setMonthDateRange(month, year);
        }
      } else if (value === DateRangePresetPeriod.byQuarter) {
        // quarter selected
        const quarter = this.selectedQuarterControl.value;
        const year = this.selectedQuarterYearControl.value;
        if (quarter != null && year != null) {
          this.setQuarterDateRange(quarter, year);
        }
      } else if (value === DateRangePresetPeriod.custom) {
        this.syncFormGroup();
      }
    }
  }

  setMonthDateRange(month: number, year: number): void {
    this.dateRange = this.getMonthDateRange(month, year);
    this.comparisonDateRange = this.getMonthDateRange(month - 1, year);
  }

  getMonthDateRange(month: number, year: number): DateRange {
    const startDate = this.useLocalTime() ? new Date(year, month) : new Date(Date.UTC(year, month));

    const endDate = this.useLocalTime() ? endOfMonth(startDate) : new Date(getUTCEndOfMonth(startDate));

    return {
      start: startDate,
      end: endDate,
    };
  }

  setQuarterDateRange(quarter: BusinessQuarter, year: number): void {
    this.dateRange = this.getQuarterDateRange(quarter, year);
    const previousQuarter = quarter - 1;
    this.comparisonDateRange = this.getQuarterDateRange(
      (quarter + 3) % 4, // don't have to deal with negative quarter numbers
      previousQuarter < 0 ? year - 1 : year,
    );
  }

  getQuarterStartMonth(quarter: number): number {
    switch (quarter) {
      case 0:
        return 0;
      case 1:
        return 3;
      case 2:
        return 6;
      case 3:
        return 9;
      default:
        return 0;
    }
  }

  setDateRangeYear(type: string, year: number): void {
    if (type === 'month') {
      if (!this.dateRange?.start) return;
      const tempMonth = this.useLocalTime() ? this.dateRange.start.getMonth() : this.dateRange.start.getUTCMonth();
      this.setMonthDateRange(tempMonth, year);
    } else {
      this.setQuarterDateRange(this.selectedQuarter, year);
    }
  }

  getQuarterDateRange(quarter: BusinessQuarter, year: number): DateRange {
    const startMonth = this.getQuarterStartMonth(quarter);
    const startDate = new Date(year, startMonth);
    const endDate = subtractDays(new Date(year, startMonth + 3), 1);
    return {
      start: startDate,
      end: endDate,
    };
  }

  private syncFormGroup(): void {
    this.dateRangeFormGroup.setValue(
      { start: this.dateRange?.start ?? null, end: this.dateRange?.end ?? null },
      { emitEvent: false },
    );
  }

  private isFuturePeriod(period?: DateRangePresetPeriod): boolean {
    if (!period) return false;
    return period >= DateRangePresetPeriod.nextSevenDays && period <= DateRangePresetPeriod.nextTwelveMonths;
  }

  setComparisonDateRange(startDate: Date, dur: number, unit: DateDuration): void {
    const subtractedDay = subtractDays(startDate, 1);
    const comparisonEndDate = this.useLocalTime() ? endOfDay(subtractedDay) : getUTCEndOfDay(subtractedDay);

    /* For 6 months and 12 months, the date should start with 1 day late for included day */
    const calculatedComparisonStartDate = calculateStartDate(startDate, dur, unit);
    if (unit !== DateDuration.days) {
      calculatedComparisonStartDate.setDate(calculatedComparisonStartDate.getDate() + 1);
    }
    this.comparisonDateRange = { start: calculatedComparisonStartDate, end: comparisonEndDate };
  }

  setDateRange(dateRange: string): void {
    const [value, unitStr] = dateRange.split(' ');
    const today = new Date();
    // remove time from the start date (important so the selector works the same way in every timezone)
    const baseDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const unit = unitStr === 'm' ? DateDuration.months : DateDuration.days;
    const dur = Number(value);

    // Check if this is a future period
    const isFuturePeriod = this.isFuturePeriod(this.selectedPeriod());
    let startDate: Date;
    let endDate: Date;

    if (!isFuturePeriod) {
      // For past periods, end at today and go backward
      endDate = baseDate;
      const adjustedDuration = unit === DateDuration.months ? dur : dur - 1;
      const calculatedStartDate = calculateStartDate(endDate, adjustedDuration, unit);

      // For month-based ranges, add 1 day to make the range inclusive
      if (unit !== DateDuration.days) {
        calculatedStartDate.setDate(calculatedStartDate.getDate() + 1);
      }
      startDate = calculatedStartDate;
    } else {
      // For future periods, start from today and go forward
      startDate = baseDate;
      const adjustedDuration = unit === DateDuration.months ? dur : dur - 1;
      const calculatedEndDateValue = calculatedEndDate(baseDate, adjustedDuration, unit);
      if (unit !== DateDuration.days) {
        calculatedEndDateValue.setDate(calculatedEndDateValue.getDate() - 1);
      }
      endDate = calculatedEndDateValue;
    }

    // remove time from the dates (important so the selector works the same way in every timezone)
    startDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    endDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

    this.dateRange = {
      start: startDate,
      end: endDate,
    };
    this.setComparisonDateRange(startDate, dur, unit);
  }

  closeDropdown(): void {
    this.isOpen = false;
  }

  onBackdropClick(): void {
    this.closeDropdown();
  }

  handleOnApply(): void {
    const period = this.DATE_RANGE_PERIODS().find((period) => period.id === Number(this.selectedPeriod()));
    this.dateRangeDisplay.period = period?.name || '';

    // Handle All Time case - emit null
    if (this.isAllTimeSelected) {
      this.dateRangeDisplay.range = null;
      this.setPeriodDisplay();
      this.emitDateRange();
      this.emitComparisonDateRange();
      this.emitDateRangePeriod();
      this.closeDropdown();
      return;
    }

    if (!this.dateRange) return;
    if (this.useLocalTime()) {
      this.dateRangeDisplay.range = formatDateRange(this.dateRange);
    } else {
      this.dateRangeDisplay.range = formatUTCDateRange(this.dateRange);
    }
    this.setPeriodDisplay();
    this.emitDateRange();
    this.emitComparisonDateRange();
    this.emitDateRangePeriod();
    this.closeDropdown();
  }

  setPeriodDisplay(): void {
    const period = this.selectedPeriod();
    if (period) {
      switch (period) {
        case DateRangePresetPeriod.allTime:
          this.periodDisplay$$.next('GALAXY.DATE_RANGE.PERIOD_SELECTOR.ALL_TIME');
          break;
        case DateRangePresetPeriod.byMonth:
          if (!this.dateRange?.start) return;
          this.periodDisplay$$.next(
            this.useLocalTime()
              ? formatDate(this.dateRange.start, DateFormat.longMonth)
              : formatUTCDate(this.dateRange.start, DateFormat.longMonth),
          );
          break;
        case DateRangePresetPeriod.byQuarter: {
          if (!this.dateRange?.start) return;
          const year = this.useLocalTime() ? this.dateRange.start.getFullYear() : this.dateRange.start.getUTCFullYear();
          const sect = 'Q' + (this.selectedQuarter + 1) + ' ' + year;
          this.periodDisplay$$.next(sect);
          break;
        }
        default:
          if (this.dateRangeDisplay.period) this.periodDisplay$$.next(this.dateRangeDisplay.period);
      }
    }
  }

  emitDateRange(): void {
    // For All Time, emit null
    if (this.isAllTimeSelected) {
      this.selectedDateRange.emit(null);
      return;
    }

    // ensure the dates are emitted with a fully inclusive date / time
    // start: 2022-03-31T00:00:00.000Z
    // end: 2022-04-01T23:59:59.999Z
    if (!this.dateRange) return;
    const { start, end } = this.dateRange;
    if (!start || !end) return;
    this.selectedDateRange.emit({
      start: this.useLocalTime() ? startOfDay(start) : getUTCStartOfDay(start),
      end: this.useLocalTime() ? endOfDay(end) : getUTCEndOfDay(end),
    });
  }

  emitComparisonDateRange(): void {
    // For All Time, no comparison date range needed
    if (this.isAllTimeSelected) {
      return;
    }

    // ensure the dates are emitted with a fully inclusive date / time
    // start: 2022-03-31T00:00:00.000Z
    // end: 2022-04-01T23:59:59.999Z
    if (!this.comparisonDateRange) return;
    const { start, end } = this.comparisonDateRange;
    if (!start || !end) return;
    this.selectedComparisonDateRange.emit({
      start: this.useLocalTime() ? startOfDay(start) : getUTCStartOfDay(start),
      end: this.useLocalTime() ? endOfDay(end) : getUTCEndOfDay(end),
    });
  }

  emitDateRangePeriod(): void {
    const period = this.selectedPeriod();
    if (period !== undefined) {
      this.selectedDateRangePeriod.emit(period);
    }
  }

  onStartDateChange(event: MatDatepickerInputEvent<Date>): void {
    if (!event.value) return;
    const start = new Date(event.value);
    if (isNaN(start.getTime())) return;
    this.dateRange = { start };
    this.comparisonDateRange = undefined;
  }

  onEndDateChange(event: MatDatepickerInputEvent<Date>): void {
    if (!event.value) return;
    const end = new Date((event.value as unknown as { valueOf(): number }).valueOf());
    if (isNaN(end.getTime())) return;
    const start = this.dateRange?.start;
    if (start) {
      this.onDateRangeChange({ start, end });
    }
  }

  onDateRangeChange(value: DateRange): void {
    if (!value.start || !value.end) {
      return;
    }

    const today = new Date();
    const startDate = value.start;
    const endDate = value.end;
    const diffInDays = differenceInDays(endDate, startDate);
    const diffInMonths = differenceInMonths(endDate, startDate);

    if (formatDate(endDate) === formatDate(today)) {
      this.calculateSelectedPeriod(diffInDays, diffInMonths);
    } else {
      this.periodManuallySet = true;
      this.selectedPeriod.set(DateRangePresetPeriod.custom);
    }
    this.dateRange = {
      start: startDate,
      end: endDate,
    };
    const dur = diffInDays + 1;
    this.comparisonDateRange = {
      start: subtractDays(startDate, dur),
      end: this.useLocalTime() ? endOfDay(subtractDays(endDate, dur)) : getUTCEndOfDay(subtractDays(endDate, dur)),
    };
  }

  calculateSelectedPeriod(diffInDays: number, diffInMonths: number): void {
    const today = new Date();
    const isFutureRange = this.dateRange?.start && this.dateRange.start > today;

    this.periodManuallySet = true;
    if (diffInDays <= 90) {
      if (diffInDays === 7) {
        this.selectedPeriod.set(
          isFutureRange ? DateRangePresetPeriod.nextSevenDays : DateRangePresetPeriod.lastSevenDays,
        );
      } else if (diffInDays === 30) {
        this.selectedPeriod.set(
          isFutureRange ? DateRangePresetPeriod.nextThirtyDays : DateRangePresetPeriod.lastThirtyDays,
        );
      } else if (diffInDays === 90) {
        this.selectedPeriod.set(
          isFutureRange ? DateRangePresetPeriod.nextNinetyDays : DateRangePresetPeriod.lastNinetyDays,
        );
      }
    } else if (diffInMonths > 3 || (diffInDays > 90 && diffInDays <= 366)) {
      if (diffInMonths === 6) {
        this.selectedPeriod.set(
          isFutureRange ? DateRangePresetPeriod.nextSixMonths : DateRangePresetPeriod.lastSixMonths,
        );
      } else if (diffInMonths === 0) {
        this.selectedPeriod.set(
          isFutureRange ? DateRangePresetPeriod.nextTwelveMonths : DateRangePresetPeriod.lastTwelveMonths,
        );
      }
    }
  }

  protected get dateContainerCollapseClass() {
    return 'date-container-' + this.collapseViewportWidth();
  }
}
